import React, { KeyboardEvent, useRef, useState } from "react";
import {
  Box,
  List,
  ListItem,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddCommentIcon from "@material-ui/icons/AddComment";
import CircularProgress from "@material-ui/core/CircularProgress";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { Intake } from "../../types/AIChatBotTypes";
import { EmptyState } from "../shared/helpers/StatePrimitives";
import { tokens } from "../../theme/tokens";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.color.surface.sunken,
  },
  newChatButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: `1px solid ${tokens.color.border.default}`,
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: tokens.color.surface.hover,
    },
  },
  newChatIcon: {
    color: tokens.color.text.secondary,
    fontSize: "1.1em",
    flexShrink: 0,
  },
  newChatText: {
    fontFamily: tokens.type.family,
    fontSize: "0.875em",
    fontWeight: 500,
    color: tokens.color.text.primary,
  },
  sectionLabel: {
    fontFamily: tokens.type.family,
    fontSize: "0.7em",
    fontWeight: 600,
    color: tokens.color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    padding: "12px 16px 6px",
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: 0,
  },
  listItem: {
    padding: "7px 6px 7px 16px",
    cursor: "pointer",
    borderRadius: "6px",
    margin: "1px 6px",
    width: "auto",
    "&:hover $menuBtn": {
      opacity: 1,
    },
    "&:hover": {
      backgroundColor: tokens.color.surface.hover,
    },
  },
  activeItem: {
    backgroundColor: tokens.color.border.default,
    "&:hover": {
      backgroundColor: tokens.color.border.default,
    },
    "& $menuBtn": {
      opacity: 1,
    },
  },
  menuOpenItem: {
    "& $menuBtn": {
      opacity: 1,
    },
  },
  itemLabel: {
    fontFamily: tokens.type.family,
    fontSize: "0.8em",
    fontWeight: 400,
    color: tokens.color.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    minWidth: 0,
  },
  menuBtn: {
    padding: 3,
    flexShrink: 0,
    opacity: 0,
    transition: "opacity 0.1s",
    color: tokens.color.text.secondary,
    "&:hover": {
      color: tokens.color.text.primary,
      backgroundColor: "transparent",
    },
    "& svg": {
      fontSize: "1rem",
    },
  },
  menuPaper: {
    borderRadius: tokens.radius.md,
    border: `1px solid ${tokens.color.border.default}`,
    boxShadow: "none",
    minWidth: 140,
  },
  menuItem: {
    fontFamily: tokens.type.family,
    fontSize: "0.8em",
    color: tokens.color.text.primary,
    gap: "10px",
    padding: "8px 14px",
    "& svg": {
      fontSize: "1rem",
      color: tokens.color.text.secondary,
    },
  },
  menuItemDelete: {
    color: tokens.color.status.error,
    "& svg": {
      color: tokens.color.status.error,
    },
  },
  // Editing state
  editingItem: {
    padding: "5px 6px 5px 12px",
    borderRadius: "6px",
    margin: "1px 6px",
    width: "auto",
    backgroundColor: tokens.color.border.default,
  },
  editInput: {
    fontFamily: tokens.type.family,
    fontSize: "0.8em",
    color: tokens.color.text.primary,
    flex: 1,
    minWidth: 0,
    "& input": {
      padding: "2px 0",
    },
  },
  editActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  editBtn: {
    padding: 3,
    color: tokens.color.text.secondary,
    "&:hover": {
      color: tokens.color.text.primary,
      backgroundColor: "transparent",
    },
    "& svg": {
      fontSize: "0.9rem",
    },
  },
}));

interface IntakeListProps {
  intakes: Intake[];
  activeIntakeId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onClose: (id: string) => void;
  onRename: (id: string, label: string) => void;
  isCreating?: boolean;
}

const IntakeList = ({
  intakes,
  activeIntakeId,
  onSelect,
  onCreate,
  onClose,
  onRename,
  isCreating = false,
}: IntakeListProps) => {
  const classes = useStyles();

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuIntakeId, setMenuIntakeId] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>, intakeId: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuIntakeId(intakeId);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuIntakeId(null);
  };

  const handleRenameClick = () => {
    const intake = intakes.find((i) => i.intake_id === menuIntakeId);
    if (!intake) return;
    closeMenu();
    setEditingId(intake.intake_id);
    setEditValue(intake.label || "");
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleDeleteClick = () => {
    if (menuIntakeId) onClose(menuIntakeId);
    closeMenu();
  };

  const commitEdit = (id: string) => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== (intakes.find((i) => i.intake_id === id)?.label || "")) {
      onRename(id, trimmed);
    }
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, id: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(id);
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  return (
    <Box className={classes.container}>
      {/* New Chat button */}
      <Box
        className={classes.newChatButton}
        onClick={isCreating ? undefined : onCreate}
        style={{ opacity: isCreating ? 0.6 : 1, cursor: isCreating ? "default" : "pointer" }}
      >
        {isCreating ? (
          <CircularProgress size={16} style={{ color: tokens.color.text.secondary, flexShrink: 0 }} />
        ) : (
          <AddCommentIcon className={classes.newChatIcon} />
        )}
        <Typography className={classes.newChatText}>
          {isCreating ? "Creating…" : "New Chat"}
        </Typography>
      </Box>

      {/* Your chats section */}
      {intakes.length > 0 && (
        <Typography className={classes.sectionLabel}>Your chats</Typography>
      )}

      {intakes.length === 0 ? (
        <EmptyState
          icon={<ForumOutlinedIcon />}
          title="No chats yet"
          description="Start a new conversation to begin."
        />
      ) : (
        <List className={classes.list}>
          {intakes.map((intake) => {
            const isEditing = editingId === intake.intake_id;
            const isActive = activeIntakeId === intake.intake_id;
            const isMenuOpen = menuIntakeId === intake.intake_id && Boolean(menuAnchor);

            if (isEditing) {
              return (
                <ListItem
                  key={intake.intake_id}
                  className={classes.editingItem}
                  disableGutters
                >
                  <InputBase
                    inputRef={inputRef}
                    className={classes.editInput}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e as any, intake.intake_id)}
                    autoFocus
                  />
                  <Box className={classes.editActions}>
                    <IconButton
                      className={classes.editBtn}
                      onClick={() => commitEdit(intake.intake_id)}
                      title="Save"
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      className={classes.editBtn}
                      onClick={cancelEdit}
                      title="Cancel"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              );
            }

            return (
              <ListItem
                key={intake.intake_id}
                className={`${classes.listItem} ${isActive ? classes.activeItem : ""} ${isMenuOpen ? classes.menuOpenItem : ""}`}
                onClick={() => onSelect(intake.intake_id)}
                disableGutters
              >
                <Typography className={classes.itemLabel}>
                  {intake.label || "New chat"}
                </Typography>
                <IconButton
                  className={classes.menuBtn}
                  onClick={(e) => openMenu(e, intake.intake_id)}
                  title="More options"
                >
                  <MoreHorizIcon />
                </IconButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Shared context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        classes={{ paper: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem className={classes.menuItem} onClick={handleRenameClick}>
          <EditIcon />
          Rename
        </MenuItem>
        <MenuItem
          className={`${classes.menuItem} ${classes.menuItemDelete}`}
          onClick={handleDeleteClick}
        >
          <DeleteIcon />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default IntakeList;
